import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Search } from "lucide-react";
import {
    Badge,
    Button,
    Container,
    DataTable,
    Dialog,
    DialogContent,
    DialogTitle,
    Input,
    SectionHeader,
    StatusBadge,
    TableSkeleton,
    Text,
} from "@athena/ui";
import { useQuery } from "@apollo/client/react";
import { CoursesDocument, type CoursesQuery } from "../../graphql/courses.generated";
import { CoursesWithActiveAssignmentsDocument } from "../../graphql/assignments.generated";
import { AssignmentWizard } from "./AssignmentWizard";
import { useListView, type ListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";

export function AssignmentsPage() {
    const navigate = useNavigate();
    const [
        courseSearch,
        setCourseSearch,
    ] = useState("");
    const [
        wizardOpen,
        setWizardOpen,
    ] = useState(false);

    const { data, loading } = useQuery(CoursesDocument);
    // Network every visit: assigning happens on the course's own page, and coming back to a cached
    // answer would leave a course just assigned under "No active assignments".
    const {
        data: activeData,
        loading: activeLoading,
        refetch: refetchActive,
    } = useQuery(CoursesWithActiveAssignmentsDocument, { fetchPolicy: "cache-and-network" });
    const courses = data?.courses ?? [];
    const filtered = courses.filter((c) => c.title.toLowerCase().includes(courseSearch.toLowerCase()));
    const activeIds = new Set<string>(activeData?.coursesWithActiveAssignments ?? []);

    const active = filtered.filter((c) => activeIds.has(c.id));
    const inactive = filtered.filter((c) => !activeIds.has(c.id));

    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. */
    const activeView = useListView(active);
    const inactiveView = useListView(inactive);
    const openCourse = (course: CourseRow) => navigate(`/assignments/${course.id}`, { state: { course } });

    // Keyed on data, not `loading`: Apollo sets loading on every refetch, so gating
    // the whole page on it replaces live content — and unmounts any child holding
    // state or an in-flight request — every time something reloads.
    if ((loading && !data) || (activeLoading && !activeData)) return <TableSkeleton />;

    return (
        <Container className="p-6">
            <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
                <DialogContent className="flex h-[90vh] max-w-4xl flex-col gap-0 p-0">
                    <DialogTitle className="border-border border-b px-6 py-4 text-base font-semibold">
                        New Course Assignment
                    </DialogTitle>
                    <div className="min-h-0 flex-1">
                        <AssignmentWizard
                            showCourseStep
                            onClose={() => {
                                setWizardOpen(false);
                                void refetchActive();
                            }}
                        />
                    </div>
                </DialogContent>
            </Dialog>
            <SectionHeader
                eyebrow={`${courses.length} courses`}
                title="Course Assignments"
                description="View all courses and their assignments. Click a course to assign it to users or organizations."
                actions={
                    <Button onClick={() => setWizardOpen(true)}>
                        <Plus /> New Course Assignment
                    </Button>
                }
            />
            {/* Filters */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="relative min-w-52 flex-1">
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        placeholder="Search by course..."
                        className="bg-card h-9 pl-9 text-sm"
                        value={courseSearch}
                        onChange={(e) => setCourseSearch(e.target.value)}
                        type="text"
                    />
                </div>
            </div>
            <div className="flex flex-col gap-6">
                <CourseSection
                    title="Active assignments"
                    description="At least one learner is assigned and hasn't finished."
                    count={active.length}
                    view={activeView}
                    searching={courseSearch !== ""}
                    onOpen={openCourse}
                />
                <CourseSection
                    title="No active assignments"
                    description="Nobody is assigned, or every assigned learner has finished."
                    count={inactive.length}
                    view={inactiveView}
                    searching={courseSearch !== ""}
                    onOpen={openCourse}
                />
            </div>
        </Container>
    );
}

type CourseRow = CoursesQuery["courses"][number];

interface CourseSectionProps {
    title: string;
    description: string;
    count: number;
    view: ListView<CourseRow>;
    searching: boolean;
    onOpen: (course: CourseRow) => void;
}

/** One collapsible group of the course list. Open to start with, so no course is out of sight. */
function CourseSection({ title, description, count, view, searching, onOpen }: CourseSectionProps) {
    return (
        <section aria-label={title}>
            <details className="group" open>
                <summary className="hover:bg-muted -mx-2 mb-3 flex cursor-pointer list-none items-center gap-2 rounded-md px-2 py-1 [&::-webkit-details-marker]:hidden">
                    <ChevronRight
                        aria-hidden
                        className="text-muted-foreground h-4 w-4 shrink-0 transition-transform group-open:rotate-90"
                    />
                    <Text as="span" weight="semibold">
                        {title} · {count}
                    </Text>
                    <Text as="span" size="sm" muted>
                        {description}
                    </Text>
                </summary>
                <ListViewControls view={view} noun="courses" />
                <DataTable
                    columns={[
                        {
                            key: "course",
                            header: "Course",
                            className: "min-w-50",
                            cell: (course) => (
                                <div className="min-w-0">
                                    <Text weight="semibold" className="truncate text-sm">
                                        {course.title}
                                    </Text>
                                    {course.description && (
                                        <Text muted className="max-w-96 truncate text-xs">
                                            {course.description}
                                        </Text>
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: "status",
                            header: "Status",
                            cell: (course) => (
                                <StatusBadge variant={course.status === "PUBLISHED" ? "success" : "warning"}>
                                    {course.status.toLocaleLowerCase()}
                                </StatusBadge>
                            ),
                        },
                        {
                            key: "tags",
                            header: "Tags",
                            cell: (course) =>
                                course.tags.map((tag, index) => (
                                    <Badge key={index} variant="tag" className="mx-0.5 my-0.5">
                                        {tag.name}
                                    </Badge>
                                )),
                        },
                    ]}
                    rows={view.rows}
                    rowKey={(course) => course.id}
                    onRowClick={onOpen}
                    filtered={searching}
                    empty={
                        <Text muted size="sm">
                            No courses here.
                        </Text>
                    }
                    noMatches="No courses match your search."
                />
            </details>
        </section>
    );
}
